import React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useAuth } from '../../../../contexts/AuthContext';
import { Unauthorized } from '../../../../components/Unauthorized';
import { useUpdateChartSection } from '../../../../hooks/useUpdateChartSection';
import useChart from '../../../../hooks/useChart';
import { Chart } from '../../../../types/Chart/Chart';
import usePatientDoc from '../../../../hooks/usePatientDoc';
import { PatientDoc } from '../../../../types/Patient/PatientDoc';
import { NextSection } from '../../../../components/buttons/NextSection';
import { NavForward } from '../../../../components/buttons/NavForward';
import { getNextPage } from '../../../../data/navigation/getNextPage';
import { markPreviousPageCompleted } from '../../../../data/navigation/utilities/markPreviousPageCompleted';
import { NavInfo } from '../../../../types/Patient/NavInfo';
import { useUpdateUserDoc } from '../../../../hooks/useUpdateUserDoc';
import { Button } from '../../../../components/buttons/Button';
import { Heading } from '../../../../components/Heading';
import * as Sentry from '@sentry/browser';
import Checkbox from '../../../../components/forms/Checkbox';

export const Route = createFileRoute('/_authenticated/$patientId/info/check')({
  component: CareTeamPage,
});

function CareTeamPage() {
  const { patientId } = Route.useParams<{ patientId: string }>();
  const { currentUser } = useAuth();
  const chartQuery = useChart(patientId);
  const chart = chartQuery.data as Chart;
  const { mutate: mutateChartDoc } = useUpdateChartSection();
  const patientDocQuery = usePatientDoc(patientId);
  const patientInfo = patientDocQuery.data as PatientDoc;
  const { mutate: mutatePatientDoc } = useUpdateUserDoc();
  const [nextPageURL, setNextPageURL] = React.useState<string | undefined>(undefined);
  const [organDonorStatus, setOrganDonorStatus] = React.useState('');
  const [navInfo, setNavInfo] = React.useState<NavInfo | undefined>();
  const [isSubmitted, setIsSubmitted] = React.useState(false);

  const handleOrganDonorStatusChange = (status: string) => {
    setOrganDonorStatus(status);
    mutateChartDoc(
      {
        patientId: patientId,
        docData: {
          LIVING_WILL: {
            livingWill: chart?.LIVING_WILL?.livingWill ? chart.LIVING_WILL.livingWill : false,
            organDonor: status,
          },
        },
      },
      {
        onSuccess: () => {
          // alert('Successfully updated document');
        },
        onError: (error) => {
          Sentry.captureException(error);
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
        },
      },
    );
  };

  // SET ORGAN DONOR STATUS FROM FIRESTORE
  React.useEffect(() => {
    if (chart?.LIVING_WILL?.organDonor) {
      setOrganDonorStatus(chart.LIVING_WILL.organDonor);
    }
  }, [chart?.LIVING_WILL?.organDonor]);

  // MARK PREVIOUS PAGE AS COMPLETED
  React.useEffect(() => {
    if (patientInfo && patientInfo.navObj) {
      markPreviousPageCompleted({ currentPageName: 'check', patientInfo, value: true });
    }
  }, [patientInfo]);

  // GET NEXT PAGE URL
  React.useEffect(() => {
    if (chart?.PAYMENT_INFO && patientInfo.navObj) {
      (async () => {
        const awaitedUrl = await getNextPage({
          currentPage: 'check',
          chart: chart,
          patientInfo: patientInfo,
        });
        setNextPageURL(awaitedUrl);
      })();
    }
  }, [chart, patientInfo]);

  // SET LOCAL NAV INFO STATE
  React.useEffect(() => {
    if (patientInfo?.navInfo) {
      setNavInfo(patientInfo.navInfo);
    }
  }, [patientInfo]);

  // SET NAV INFO IN FIRESTORE
  React.useEffect(() => {
    if (navInfo) {
      mutatePatientDoc(
        {
          patientId: patientId,
          navInfo: navInfo,
        },
        {
          onSuccess: () => {
            // alert('Successfully updated document');
          },
          onError: (error) => {
            Sentry.captureException(error);
            alert(`Error updating document: ${error.message}`); // TELL THE USER
            console.error(error); // GET THE STACKTRACE
          },
        },
      );
    }
  }, [navInfo]);

  if (chartQuery.isLoading || !chart) {
    return <div>Loading patient information...</div>;
  }
  if (!currentUser) {
    return <div>Loading current user</div>;
  }
  if (currentUser?.uid !== patientId) {
    return <Unauthorized />;
  }
  if (currentUser?.uid === patientId && chart?.PATIENT_INFO_1) {
    return (
      <>
        <div id="health-insurance-page" className="flex w-screen flex-col items-center justify-center">
          <Heading id="health-insurance-page-header" textContent="Care Team & Directives" />
          <div className="flex flex-col px-12">
            <h2 className="mt-4 text-2xl font-bold">My Care Team</h2>
            <form>
              <Checkbox
                id="hca"
                name="has-hca"
                label="I have a named healthcare agent. This is required."
                checked={true}
                disabled={true}
                onChange={() => {
                  return null;
                }}
              />
              <button
                className="ml-2 text-xl text-blue-500 underline"
                onClick={() => {
                  alert(
                    'A healthcare agent is someone who can speak for you during a medical emergency when you cannot speak for yourself.\r\rIf you have not yet named a healthcare agent, you can name one here today, but you should be sure to communicate this to your newly named agent.\r\rIf you are unwilling/unable to name an agent today, you will need to obtain a MIDEO through our guided process. Please call 1-844-MIDEO-4U to speak to a concierge.',
                  );
                }}
              >
                More Info
              </button>
              <Checkbox
                id="has-doctor"
                name="hasDoctor"
                label="I have a physician who should receive a copy of my MIDEO and information."
                checked={patientInfo?.navInfo?.hasDoctor}
                onChange={() => {
                  if (navInfo) {
                    const navInfoClone = { ...navInfo };
                    navInfoClone.hasDoctor = !navInfo.hasDoctor;
                    setNavInfo(navInfoClone);
                  }
                }}
              />
              <Checkbox
                id="has-hospital"
                name="hasHospital"
                label="I have a preferred hospital. I would like them to receive a copy of my MIDEO and information to keep on file."
                checked={patientInfo?.navInfo?.hasHospital}
                onChange={() => {
                  if (navInfo) {
                    const navInfoClone = { ...navInfo };
                    navInfoClone.hasHospital = !navInfo.hasHospital;
                    setNavInfo(navInfoClone);
                  }
                }}
              />
              <Checkbox
                id="has-attorney"
                name="hasAttorney"
                label="I have an attorney with whom I would like to share my MIDEO and information."
                checked={patientInfo?.navInfo?.hasAttorney}
                onChange={() => {
                  if (navInfo) {
                    const navInfoClone = { ...navInfo };
                    navInfoClone.hasAttorney = !navInfo.hasAttorney;
                    setNavInfo(navInfoClone);
                  }
                }}
              />
              {patientInfo.navType.base !== 'self' && (
                <div>
                  <Checkbox
                    id="uploading-documents"
                    name="uploadingDocuments"
                    label="I have paper based documents (POLST, living will, medical durable power of attorney, etc.) that I would like to upload to share on my MIDEO via QR code."
                    checked={patientInfo?.navInfo?.uploadingDocuments}
                    onChange={() => {
                      if (navInfo) {
                        const navInfoClone = { ...navInfo };
                        navInfoClone.uploadingDocuments = !navInfo.uploadingDocuments;
                        setNavInfo(navInfoClone);
                      }
                    }}
                  />
                </div>
              )}
              <h2 className="mt-4 text-2xl font-bold">Other Directives</h2>
              <fieldset>
                <legend className="text-xl font-bold">Decision Regarding Organ Donation:</legend>
                <div>
                  <div className="ml-4">
                    <input
                      type="radio"
                      id="organDonor"
                      name="organDonorStatus"
                      value="Yes"
                      checked={organDonorStatus === 'Yes'}
                      onChange={(e) => handleOrganDonorStatusChange(e.target.value)}
                    />
                    <label className="ml-2 text-xl" htmlFor="organDonor">
                      I am an organ donor
                    </label>
                  </div>

                  <div className="ml-4">
                    <input
                      type="radio"
                      id="notOrganDonor"
                      name="organDonorStatus"
                      value="No"
                      checked={organDonorStatus === 'No'}
                      onChange={(e) => handleOrganDonorStatusChange(e.target.value)}
                    />
                    <label className="ml-2 text-xl" htmlFor="notOrganDonor">
                      I am not an organ donor
                    </label>
                  </div>

                  <div className="ml-4">
                    <input
                      type="radio"
                      id="TBD"
                      name="organDonorStatus"
                      value="TBD"
                      checked={organDonorStatus === 'TBD'}
                      onChange={(e) => handleOrganDonorStatusChange(e.target.value)}
                    />
                    <label className="ml-2 text-xl" htmlFor="TBD">
                      My organ donation status is undecided
                    </label>
                  </div>
                </div>
              </fieldset>
            </form>
            <div className="flex flex-col items-center">
              <Button
                id="submit-info"
                textContent="Submit"
                type="button"
                onClick={() => setIsSubmitted(true)}
                disabled={!chart?.LIVING_WILL?.organDonor}
                submitted={isSubmitted}
              />
              <div id="nav" className="flex flex-row">
                {patientInfo?.navType?.base === 'self' &&
                  nextPageURL &&
                  isSubmitted &&
                  organDonorStatus?.length > 0 && (
                    <NavForward to={nextPageURL} disabled={organDonorStatus.length === 0} />
                  )}
                {patientInfo?.conciergeNavigator?.educationUnlocked && (
                  <div className="place-self-center">
                    {nextPageURL && organDonorStatus?.length > 0 && isSubmitted && <NextSection to={nextPageURL} />}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }
  return <div>Loading...</div>;
}
